import { GetStaticProps } from 'next';
import destinationsListCache from 'scripts/cache/destinationsList.json';
import { createActivityPageStructuredData } from '@/entities/Activity/app/Creators';
import { fetchReviews } from '@/entities/Reviews/service/fetchReviews';
import { fetchActivityReviewed } from '@/entities/Reviews/service/fetchActivityReviewed';
import { ReviewsPage } from '@/screens/ReviewsPage';
import { ReviewsPageProps } from '@/screens/ReviewsPage/ReviewsPage';
import { getRedirectsPath } from '@/common/service/api/Redirects/getRedirectsPath';

const LIMIT_REVIEWS = 24;

export async function getStaticPaths() {
    return {
        paths: [],
        fallback: 'blocking',
    };
}

type PageStaticProps = {
    ActivityId: string;
    ActivitySlug: string;
};

export const getStaticProps: GetStaticProps<ReviewsPageProps, PageStaticProps> = async ({
    params,
}) => {
    try {
        if (!params) {
            throw new Error(`PAGE REVIEWS: BAD PARAMS: ${params}`);
        }

        const [reviews, activity] = await Promise.all([
            fetchReviews({
                limit: LIMIT_REVIEWS,
                activity_id: params.ActivityId,
                offset: 0,
            }),
            fetchActivityReviewed({
                id: params.ActivityId,
                is_full: 1,
                destination_id: '',
            }),
        ]);

        const currentActivity = activity.items[0];
        const currentPath = `/${params.ActivitySlug}/details/${params.ActivityId}/reviews/`;

        if (!activity) {
            const redirectPath = await getRedirectsPath(currentPath);

            if(redirectPath === '/404/'){
                return {
                    notFound: true,
                };
            }
            
            return {
                revalidate: Number(process.env.CACHE_ENV),
                redirect: {
                    destination: redirectPath.toLowerCase(),
                    statusCode: 301,
                },
            };
        }

        const rightPath =
            `/${currentActivity.slug}/details/${currentActivity.id}/reviews/`.toLowerCase();

        if (currentPath !== rightPath) {
            return {
                revalidate: Number(process.env.CACHE_ENV),
                redirect: {
                    destination: rightPath,
                    statusCode: 301,
                },
            };
        }

        const currentDestination =
            destinationsListCache.find((i) => i.id === currentActivity.city_id) ||
            destinationsListCache.find((i) => i.id === currentActivity.locations[0].city_id) ||
            destinationsListCache[0];

        const currentCategory = currentActivity.activity_categories[0];
        const categorySlug =
            !!currentCategory &&
            currentDestination.categories.find((cat) => cat.id === currentCategory.id)?.slug;

        if (!currentActivity.is_active) {
            const defaultPath = currentDestination.categories.some(
                (i) => i.id === currentCategory?.id
            )
                ? currentCategory.url
                : currentDestination.url_search;
            const redirectPath = await getRedirectsPath(defaultPath);

            if(redirectPath === '/404/'){
                return {
                    notFound: true,
                };
            }
            
            return {
                revalidate: Number(process.env.CACHE_ENV),
                redirect: {
                    destination: redirectPath.toLowerCase(),
                    statusCode: 301,
                },
            };
        }

        const structuredData = createActivityPageStructuredData(currentActivity);

        const breadcrumbs = [
            {
                url: currentDestination.url.toLowerCase(),
                title: currentDestination.name,
            },
            {
                url: `/destination/${currentDestination.state.code}/${currentDestination.slug}/things-to-do/`.toLowerCase(),
                title: 'Things to do',
            },
            ...(categorySlug
                ? [
                      {
                          url: `/destination/${currentDestination.state.code}/${currentDestination.slug}/things-to-do/${categorySlug}/`.toLowerCase(),
                          title: currentActivity.activity_categories[0].name,
                      },
                  ]
                : []),
            {
                url: currentActivity.url.toLowerCase(),
                title: currentActivity.name,
            },
            {
                url: `${currentActivity.url}reviews/`.toLowerCase(),
                title: 'Reviews',
                unlink: true,
            },
        ];

        return {
            props: {
                activity: activity.items[0],
                reviews: reviews?.items || [],
                breadcrumbs,
                structuredData,
                currentDestination,
                currentCategory: currentCategory || null,
            },
            revalidate: 3600,
        };
    } catch (e) {
        throw new Error((e as Error).message);
    }
};

export default ReviewsPage;
